import { useCallback, useState, useRef, ChangeEvent, DragEvent, useEffect, useMemo } from 'react';
import { AttachFile, Delete, Folder, Link } from '@mui/icons-material';
import useStyles from './styles';

import Colors from '../../entities/colors';

interface UploadFileProps {
  file?: string | ArrayBuffer | File | null;
  readonly?: boolean;
  errorMessage?: string;
  error?: Boolean;
  onUpload: (file: File | null, base64?: string | ArrayBuffer | null) => any;
}

export const UploadFile = ({ file, readonly = false, error, errorMessage, onUpload }: UploadFileProps): JSX.Element => {
  const classes = useStyles();
  const [preview, setPreview] = useState(file);
  const [dragging, setDragging] = useState(false);
  const inputFile = useRef<any>(null);

  const [dragCounter, setDragCounter] = useState(0);

  useEffect(() => {
    setPreview(file);
  }, [file]);

  const handleDragEnter = (event: DragEvent) => {
    event.stopPropagation();
    event.preventDefault();

    setDragCounter(dragCounter + 1);

    setDragging(true);
  };

  const handleImageChange = useCallback(
    (currentFile: File) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        onUpload(currentFile, reader.result);
        setPreview(currentFile);
      };

      reader.readAsDataURL(currentFile);
    },
    [onUpload]
  );

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    setDragging(false);
    setDragCounter(0);
    const files = event.dataTransfer?.files;

    handleImageChange(files[0]);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const currentCounter = dragCounter - 1;

    if (currentCounter === 0) {
      setDragging(false);
    }
    setDragCounter(currentCounter);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const files = event.target?.files || [];

    handleImageChange(files[0]);
  };

  const handleRemoveImg = useCallback(() => {
    setPreview(null);
    onUpload(null, null);
  }, [onUpload]);

  const openLink = useCallback(() => {
    if (file && file !== null && typeof file === 'string') {
      window.open(file, '_blank')?.focus();
    }
  }, [file]);

  const renderPreview = useCallback(() => {
    const fileString =
      typeof preview === 'string'
        ? new URL(decodeURIComponent(preview)).pathname.split('/').pop()
        : (preview as File).name;
    const [fileName, extension] = fileString?.split('.') || [];
    return (
      <div onClick={() => (readonly ? openLink() : handleRemoveImg())} className={classes.preview}>
        <div>
          {readonly ? (
            <Link sx={{ fontSize: '2rem', color: Colors.N100 }} />
          ) : (
            <Delete sx={{ fontSize: '2rem', color: Colors.N100 }} />
          )}
        </div>
        <div className={classes.document}>
          <Folder fontSize="large" />
          <div className={classes.documentName}>
            <div>{fileName}</div>.<div>{extension}</div>
          </div>
        </div>
      </div>
    );
  }, [readonly, preview, classes.preview, classes.documentName, classes.document, handleRemoveImg, openLink]);

  const renderDefaultContent = useCallback(
    () => (
      <div>
        <AttachFile sx={{ fontSize: '52px', color: Colors.N100 }} />
        <h4 className={classes.helpText}>
          Arraste seu arquivo aqui, ou{' '}
          <div className={classes.link} onClick={() => inputFile?.current?.click()}>
            carregue
          </div>
        </h4>
      </div>
    ),
    [classes.link, classes.helpText]
  );

  const getClasses = useMemo(() => {
    if (error) {
      return `${classes.dragArea} ${classes.dragAreaError}`;
    }

    return dragging ? classes.dragging : classes.dragArea;
  }, [classes.dragging, classes.dragArea, classes.dragAreaError, dragging, error]);

  return (
    <div className={classes.root}>
      <input
        ref={inputFile}
        style={{ visibility: 'hidden', width: 0, height: 0 }}
        type="file"
        onChange={handleChange}
      />
      <div className={classes.dragContainer}>
        <div
          className={getClasses}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {file && preview ? renderPreview() : renderDefaultContent()}
        </div>
      </div>
      <div className={classes.errorText}>{errorMessage}</div>
    </div>
  );
};

export default UploadFile;
